import { type ReactNode, useCallback, useState } from 'react';
import { api, type IptvRecommendedSource } from '../api.js';
import { PlusIcon, SparklesIcon } from '../Icon.tsx';

/**
 * Önerilen çalma listeleri — **sunucudan ölçülmüş**.
 *
 * ## Neden ayrı bileşen?
 *
 * "Listemde 300 kanal var ama 5'i açılıyor" sorusunun cevabı, listenin
 * kalitesinde değil **sunucunun bulunduğu ülkede** aranır. Bu panel
 * ölçümü pbfork sunucusundan yapar: gördüğü sayı, oynatırken karşılaşacağı
 * sayıyla aynıdır.
 *
 * ## Yasal mı?
 *
 * Listeler yalnızca **resmî olarak ücretsiz** yayınları içerir: FTA
 * yayınları ve reklam destekli FAST servisleri (Pluto TV, Samsung TV Plus,
 * Roku).
 */

/** Ölçüm sonucu kodları → okunur etiket. */
const REASON_LABELS: Record<string, string> = {
  ok: 'açılıyor',
  blocked: 'coğrafi kısıt (401/403)',
  not_found: 'ölü kanal (404)',
  timeout: 'zaman aşımı',
  network: 'ağ hatası',
  blocked_address: 'engelli adres',
  error: 'hata',
};

export function LiveTvRecommendedSources({
  onAdd,
  busy,
}: {
  /** Ölçülen listeyi kaynak olarak ekler. */
  onAdd: (source: IptvRecommendedSource) => void;
  busy: boolean;
}): ReactNode {
  const [sources, setSources] = useState<IptvRecommendedSource[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const test = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setSources(await api.iptvRecommendedSources(true));
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Listeler ölçülemedi.');
    } finally {
      setLoading(false);
    }
  }, []);

  return (
    <div className="live-tv__sources">
      <div className="live-tv__header-left">
        <h2 className="live-tv__subtitle">Listeleri test et</h2>
      </div>
      <p className="live-tv__sources-hint">
        Aşağıdaki yasal listeler <strong>bu sunucudan</strong> ölçülür: kaç kanalın gerçekten
        açıldığı, neden açılmadığı görünür.
      </p>
      <div>
        <button
          type="button"
          className="btn btn--ghost"
          onClick={() => void test()}
          disabled={loading}
        >
          <SparklesIcon size={16} />
          <span>{loading ? 'Listeler ölçülüyor…' : 'Listeleri ölç'}</span>
        </button>
      </div>

      {error !== null && <p className="live-tv__error">{error}</p>}

      {sources !== null && (
        <ul className="live-tv__source-list">
          {sources.map((source) => {
            const probe = source.probe;
            const reasons = Object.entries(probe.reasons)
              .filter(([reason]) => reason !== 'ok')
              .map(([reason, count]) => `${REASON_LABELS[reason] ?? reason}: ${count}`)
              .join(' · ');
            const isWorking = probe.error === null && probe.working > 0;

            return (
              <li key={source.id} className="live-tv__source">
                <span className="live-tv__source-body">
                  <span className="live-tv__source-title-row">
                    <span className="live-tv__source-name">{source.name}</span>
                    {isWorking && (
                      <span
                        className="live-tv__source-type-pill"
                        style={{ color: 'var(--success)' }}
                      >
                        Yasal & Ücretsiz
                      </span>
                    )}
                  </span>
                  <span className="live-tv__source-meta">
                    <span
                      className={`live-tv__source-status-dot${
                        isWorking ? '' : ' live-tv__source-status-dot--error'
                      }`}
                      aria-hidden="true"
                    />
                    <span>
                      {probe.error !== null
                        ? `Liste okunamadı: ${probe.error}`
                        : `${probe.working}/${probe.tested} kanal açılıyor (toplam ${probe.total})`}
                    </span>
                  </span>
                  <span className="live-tv__source-meta">{source.note}</span>
                  {reasons !== '' && (
                    <span className="live-tv__source-meta" style={{ opacity: 0.6 }}>
                      {reasons}
                    </span>
                  )}
                </span>
                <div className="live-tv__source-actions">
                  <button
                    type="button"
                    className="btn btn--primary"
                    style={{ padding: '6px 14px', fontSize: 'var(--fs-xs)' }}
                    onClick={() => onAdd(source)}
                    disabled={busy || probe.error !== null}
                  >
                    <PlusIcon size={14} />
                    <span>Ekle</span>
                  </button>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
